import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDateTimePicker,
} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputAdornment,
  Stack,
  SxProps,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';

import { useAppSelector } from '../../../../../hooks';
import { getUrlConvertedString } from '../../../../../helper';
import { TP_COLORS } from '../../../../../constants';
import { TSubSectionCreateNewItemRequestData } from '../../components/SectionItem';

import {
  THomeworkFile,
  THomeworkQuiz,
} from '../../../../../store/V2/HomeworksSlice';
import {
  createCourseHomeworkSectionAsUser,
  ISection,
  TCreateCourseHomeworkSectionAsUser,
  TUpdateCourseHomeworkSectionAsUser,
  updateCourseHomeworkSectionAsUser,
} from '../../../../../store/V2/SectionSlice';
import { getCoursesState } from '../../../../../store/V2/CoursesSlice';
import { IQuiz } from '../../../../../store/QuizSlice';

import { TPTextField } from '../../../../../components/Inputs';
import ShareOptions from './ShareOptions';
import UploadHomeworkFiles from './UploadHomeworkFiles';
import QuizzesList from './QuizzesList';
import QuizFilterSection from './QuizFilterSection';

export const getQuizLink = (quiz: IQuiz): string => {
  const curriculum = getUrlConvertedString(quiz.topic?.curriculum || '');
  const subject = getUrlConvertedString(quiz.topic?.subject || '');
  const urlTopic = getUrlConvertedString(quiz.topic?.name || '');
  const subTopic = getUrlConvertedString(
    quiz.subTopics[0]?.subTopic?.name || ''
  );
  const subTopicId = quiz.subTopics[0]?.subTopic?._id;

  return `${process.env.REACT_APP_TUTOPIYA_ASSESSMENT_URL}/dashboard/${curriculum}/${subject}/${urlTopic}/${subTopic}/${subTopicId}/quiz`;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultButtonStyles: SxProps = {
  padding: { xs: '6px 12px', sm: '12px 24px' },
  fontWeight: '600',
  borderRadius: '5px',
};

export type TViews = 'select' | 'file' | 'link' | 'quiz';

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  title: string;
  dueDateTime: Moment;
  description: string;
  link: string;
  files: THomeworkFile[];
  quizzes: THomeworkQuiz[];
  selectedCurriculum: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  selectedGrade: TSelectOption | null;
};

const initialState: TState = {
  title: '',
  dueDateTime: moment().add(3, 'days'),
  description: '',
  link: '',
  files: [],
  quizzes: [],
  selectedCurriculum: null,
  selectedSubject: null,
  selectedGrade: null,
};

interface AddHomeworkModalProps {
  open: boolean;
  onClose: () => void;
  parentSectionItemData?: TSubSectionCreateNewItemRequestData['parentSection'];
  edit?: boolean;
  section?: ISection;
  prevSection?: ISection;
}

const AddHomeworkModal: React.FC<AddHomeworkModalProps> = ({
  open,
  onClose,
  parentSectionItemData,
  edit,
  section,
  prevSection,
}) => {
  const dispatch = useDispatch();

  // Store
  const { userCourse: courseData } = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);
  const [view, setView] = useState<TViews>('select');

  const isSubmitDisabled =
    !state.title.trim().length ||
    !state.dueDateTime ||
    !state.description.trim().length;

  useEffect(() => {
    if (edit && section) {
      setState((prevState) => {
        const newState: TState = {
          ...prevState,
          title: section.title,
          description: section.description,
        };

        if (section.dueDate) {
          newState.dueDateTime = moment(section.dueDate);
        }

        return newState;
      });
    }

    return () => {
      setState(initialState);
    };
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateTimeChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        dueDateTime: value.startOf('minute'),
      }));
    }
  };

  const handleViewChangeClick = (value: TViews) => {
    setView(value);
  };

  const handleFinishedUpload = async (files) => {
    setState((prevState) => ({
      ...prevState,
      files,
    }));
  };

  const handleQuizSelect = (quiz: IQuiz) => {
    const findQuiz = state.quizzes.find((item) => item._id === quiz._id);
    let newQuizzes: THomeworkQuiz[] = [];
    if (findQuiz) {
      newQuizzes = state.quizzes.filter((newFile) => newFile._id !== quiz._id);
    } else {
      const newQuiz = {
        _id: quiz._id,
        difficulty: quiz.difficulty,
        topic: quiz.topic.name,
        subTopic: quiz.subTopics[0].subTopic.name,
        noOfQuestions: quiz.noOfQuestions,
        link: getQuizLink(quiz),
      };

      newQuizzes = [...state.quizzes, newQuiz];
    }

    setState((prevState) => ({
      ...prevState,
      quizzes: newQuizzes,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (edit && section) {
      const apiData: TUpdateCourseHomeworkSectionAsUser = {
        _id: section._id,
        title: state.title.trim(),
        dueDate: state.dueDateTime.clone().toISOString(),
        description: state.description,
      };

      await dispatch(updateCourseHomeworkSectionAsUser(apiData));
    } else if (courseData) {
      const sortingKey = prevSection
        ? prevSection.sortingKey + 0.1
        : parentSectionItemData?.nextSortingKey;

      if (sortingKey) {
        const apiData: TCreateCourseHomeworkSectionAsUser = {
          sortingKey,
          parent: parentSectionItemData?._id,
          course: courseData._id,
          title: state.title.trim(),
          dueDate: state.dueDateTime.clone().toISOString(),
          description: state.description,
        };

        if (state.files.length) {
          apiData.files = state.files;
        }

        if (state.link) {
          apiData.link = state.link;
        }

        if (state.quizzes.length) {
          apiData.quizzes = state.quizzes;
        }

        await dispatch(createCourseHomeworkSectionAsUser(apiData));
      }
    }

    setLoading(false);

    onClose();
  };

  const renderRightSection = () => {
    switch (view) {
      case 'select':
        return <ShareOptions onClick={handleViewChangeClick} />;
      case 'file':
        return <UploadHomeworkFiles onFinish={handleFinishedUpload} />;
      case 'quiz':
        return (
          <QuizzesList
            quizzes={state.quizzes}
            handleQuizSelect={handleQuizSelect}
          />
        );
      case 'link':
        return (
          <Stack
            height="100%"
            justifyContent="center"
            alignItems="center"
            sx={{ background: '#F9F9F9' }}
          >
            <FormControl sx={{ width: '70%' }}>
              <TPTextField
                multiline
                rows={3}
                name="link"
                size="small"
                label="Link *"
                placeholder="Link"
                value={state.link}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </Stack>
        );
      default:
        return null;
    }
  };

  const getMaxWidth = () => {
    if (edit) return 'sm';
    if (view === 'quiz') return 'lg';
    return 'md';
  };

  const getCount = () => {
    if (edit) return 12;
    if (view === 'quiz') return 5;
    return 6;
  };

  return (
    <Dialog
      fullWidth
      maxWidth={getMaxWidth()}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '1024px', padding: '30px' },
      }}
    >
      <Typography fontSize="16px" fontWeight="600">
        {edit ? 'Update' : 'Upload'} Homework
      </Typography>
      <DialogContent sx={{ p: '20px 0px 30px' }}>
        <Grid container spacing={2}>
          <Grid item xs={getCount()}>
            <Typography fontSize="14px" color="#475569">
              Enter the homework details, set the due date, and choose the
              homework type. Ensure everything is ready for your students!
            </Typography>
            <Grid
              container
              spacing={2}
              paddingRight={2}
              marginTop="30px"
              sx={{
                maxHeight: 'calc(100vh - 310px)',
                overflowY: 'auto',
              }}
            >
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TPTextField
                    {...textFieldProps}
                    name="title"
                    size="small"
                    label="Title *"
                    placeholder="Title"
                    value={state.title}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDateTimePicker
                      value={state.dueDateTime}
                      minDateTime={moment()}
                      onChange={handleDateTimeChange}
                      inputFormat="YYYY-MM-DD hh:mm A"
                      label="Due Date *"
                      InputProps={{ readOnly: true }}
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          placeholder="Select Date Time"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <InputAdornment position="end">
                                <DateRangeIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TPTextField
                    multiline
                    rows={3}
                    name="description"
                    size="small"
                    label="Description *"
                    placeholder="Description"
                    value={state.description}
                    onChange={handleInputChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </FormControl>
              </Grid>
              {view === 'quiz' ? <QuizFilterSection /> : null}
            </Grid>
          </Grid>
          {!edit ? (
            <Grid item xs={12} sm={view === 'quiz' ? 7 : 6}>
              {renderRightSection()}
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={{
            ...defaultButtonStyles,
            color: TP_COLORS.gray.darken,
            backgroundColor: TP_COLORS.border.color1,
          }}
        >
          Back
        </Button>
        <LoadingButton
          disableElevation
          variant="contained"
          color="secondary"
          loading={isLoading}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          sx={{
            ...defaultButtonStyles,
            color: '#F8FAFC',
            backgroundColor: TP_COLORS.gray.darken,
          }}
        >
          {edit ? 'Update' : 'Upload'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddHomeworkModal;
